import { useEnv } from '@sparklayer-io/spark-dash-core/context/env-context';
import { SearchInput } from '@sparklayer-io/spark-dash-pages/components/search-input';
import { withPageProviders } from '@sparklayer-io/spark-dash-pages/context/page-context';
import { SyncLogStatusAlert } from '@sparklayer-io/spark-dash-pages/features/sync';
import { SyncLogErrorTable } from '@sparklayer-io/spark-dash-pages/features/sync/components/sync-log-error-table';
import { useSyncStatus } from '@sparklayer-io/spark-dash-pages/features/sync/hooks/use-sync-log-status';
import { useTriggerManualSync } from '@sparklayer-io/spark-dash-pages/features/sync/hooks/use-trigger-manual-sync';
import type { SyncLogIdentifier } from '@sparklayer-io/spark-dash-pages/features/sync/types';
import { Card, EmptyState, Page, Skeleton } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';

export const StaffCustomerSyncPage = withPageProviders(() => {
  const env = useEnv();
  const syncLogId: SyncLogIdentifier = { integration: env.platform ?? '', dataType: 'customers' };
  const { data: status, isLoading, isFetching, error, refetch } = useSyncStatus(syncLogId);
  const { triggerManualSync, isSyncing } = useTriggerManualSync({ syncFor: syncLogId });
  const [searchValue, setSearchValue] = useState<string>('');

  const loadFailed = Boolean(error) || (!isLoading && !status);
  const isSyncLogErrors = (status?.error_count ?? 0) > 0;

  return (
    <Page
      title={t('features:customers.pages.staff_customer_sync_page.title')}
      description={t('features:customers.pages.staff_customer_sync_page.description')}
      primaryAction={{
        label: t('features:customers.pages.staff_customer_sync_page.actions.manual_sync'),
        onClick: () => triggerManualSync(),
      }}
      secondaryActions={[
        {
          label: t('buttons:reload'),
          onClick: () => refetch(),
          loading: isFetching,
        },
      ]}
      learnMoreUrl="https://docs.sparklayer.io/customers"
      footerDescription={t('features:customers.pages.staff_customer_sync_page.footer')}
    >
      {loadFailed ? (
        <Card>
          <EmptyState
            kind="error"
            title={t('features:synclog.components.sync_log_status_alert.load_error.title')}
            description={t(
              'features:synclog.components.sync_log_status_alert.load_error.description',
            )}
            onRetry={() => void refetch()}
          />
        </Card>
      ) : isLoading || isSyncing ? (
        <Skeleton />
      ) : (
        <div className="w-full">
          <SyncLogStatusAlert syncFor={syncLogId} allowViewErrors={false} />
          {isSyncLogErrors && (
            <>
              <div className="mt-4">
                <SearchInput
                  onSearchChange={setSearchValue}
                  placeholder={t(
                    'features:customers.pages.staff_customer_sync_page.search_placeholder',
                  )}
                />
              </div>
              <div className="mt-4">
                <SyncLogErrorTable
                  syncFor={syncLogId}
                  showApiErrors={true}
                  searchValue={searchValue}
                />
              </div>
            </>
          )}
        </div>
      )}
    </Page>
  );
});
